import { useState } from "react";
import { Star, Play, Youtube, Facebook, PenLine } from "lucide-react";
import { Reveal, Overline } from "./Motion";
import { BIZ, REVIEWS, VIDEOS, TRUST } from "../data/site";

export const TrustStrip = () => (
  <section className="border-b border-slate-200 bg-white" data-testid="trust-strip">
    <div className="wrap grid grid-cols-2 divide-slate-200 md:grid-cols-5 md:divide-x [&>*:last-child]:col-span-2 md:[&>*:last-child]:col-span-1">
      {TRUST.map((t, i) => (
        <Reveal key={t.small} delay={i * 0.06}>
          <div className="px-2 py-7 md:px-6" data-testid={`trust-${i}`}>
            <span className="font-display block text-4xl font-black leading-none text-slate-900">{t.big}</span>
            <span className="mt-1 block text-xs font-bold uppercase tracking-widest text-amber-700">{t.small}</span>
            <span className="text-xs text-slate-500">{t.sub}</span>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const LeaveReview = ({ testid, className = "" }) => (
  <a href={BIZ.googleReview} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 font-semibold text-white transition-colors hover:bg-slate-700 ${className}`} data-testid={testid}>
    <PenLine className="h-4 w-4" /> Leave us a Google review
  </a>
);

export const Reviews = () => (
  <section className="bg-slate-50 py-20 md:py-28" data-testid="reviews-section">
    <div className="wrap">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div><Overline>Real reviews</Overline><h2 className="font-display text-4xl font-black uppercase leading-[0.9] text-slate-900 md:text-6xl">Neighbors say it best</h2></div>
        <div className="flex flex-wrap gap-2 text-sm">
          <LeaveReview testid="reviews-leave-google-review" />
          <a href={BIZ.google} target="_blank" rel="noreferrer" className="rounded-full border border-slate-300 px-4 py-2 font-semibold hover:border-slate-900" data-testid="reviews-google-link">5.0★ on Google</a>
          <a href={BIZ.bbb} target="_blank" rel="noreferrer" className="rounded-full border border-slate-300 px-4 py-2 font-semibold hover:border-slate-900" data-testid="reviews-bbb-link">A on BBB</a>
        </div>
      </Reveal>
      <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {REVIEWS.map((r, i) => (
          <Reveal key={r.name} delay={(i % 3) * 0.07}>
            <figure className="flex h-full flex-col rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200" data-testid={`review-${i}`}>
              <div className="flex gap-0.5">{[0, 1, 2, 3, 4].map((s) => <Star key={s} className="h-4 w-4 fill-slate-900 text-slate-900" />)}</div>
              <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-slate-700">“{r.text}”</blockquote>
              <figcaption className="mt-5 flex items-center justify-between gap-2 text-sm"><span><span className="block font-bold text-slate-900">{r.name}</span><span className="text-xs text-slate-500">{r.tag}</span></span><span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">Verified customer</span></figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Video = ({ v, i }) => {
  const [play, setPlay] = useState(false);
  return (
    <div className="group relative aspect-video overflow-hidden rounded-2xl bg-slate-900" data-testid={`video-${i}`}>
      {play ? (
        <iframe className="absolute inset-0 h-full w-full" src={`https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1&rel=0`} title={v.title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
      ) : (
        <button onClick={() => setPlay(true)} className="absolute inset-0 text-left" data-testid={`video-play-${i}`} aria-label={`Play ${v.title}`}>
          <img src={`/img/yt/${v.id}.jpg`} alt={v.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
          <span className="absolute inset-0 bg-gradient-to-t from-slate-950/85 to-transparent" />
          <span className="absolute left-1/2 top-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white text-slate-950 transition-transform duration-300 group-hover:scale-110"><Play className="ml-0.5 h-6 w-6 fill-slate-950" /></span>
          <span className="absolute bottom-4 left-4 right-4 text-sm font-semibold text-white">{v.title}</span>
        </button>
      )}
    </div>
  );
};

export const Videos = () => (
  <section className="bg-slate-100 py-20 text-slate-900 md:py-28" data-testid="videos-section">
    <div className="wrap">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div><Overline>Watch our work</Overline><h2 className="font-display text-4xl font-black uppercase leading-[0.9] md:text-6xl">Real projects, real homes</h2></div>
        <div className="flex gap-2">
          <a href={BIZ.youtube} target="_blank" rel="noreferrer" className="btn-ghost-dark" data-testid="videos-youtube-link"><Youtube className="h-4 w-4" />YouTube</a>
          <a href={BIZ.facebook} target="_blank" rel="noreferrer" className="btn-ghost-dark" data-testid="videos-facebook-link"><Facebook className="h-4 w-4" />Facebook</a>
        </div>
      </Reveal>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {VIDEOS.map((v, i) => <Reveal key={v.id} delay={(i % 3) * 0.07}><Video v={v} i={i} /></Reveal>)}
      </div>
    </div>
  </section>
);
